<template>
	<div>
		<div
			v-if="widgetActive"
			class="hl-detection-status"
		>
			<template v-if="isCreated || isProcessing">
				<LoadingSpinner
					class="hl-detection-status__leading"
					color="light"
				/>
				<div class="hl-detection-status__text">
					<template v-if="progressPct === 0">
						{{ $I18n.trans('highlights.audio_detection.tagging.status.starting') }}
					</template>
					<template v-else>
						{{ $I18n.trans('highlights.audio_detection.tagging.status.running') }} ({{ progressPct }}%)
					</template>
				</div>
			</template>
			<div
				v-else-if="isCompleted"
				class="hl-detection-status__completed"
			>
				<template v-if="job.createdTags > 0 || job.ratedTags > 0">
					<div class="hl-detection-status__title">
						{{ $I18n.trans('highlights.audio_detection.tagging.status.completed') }}
					</div>
					<Icon
						class="hl-detection-status__icon"
						icon-name="fireworks"
						size="xxxxl"
					/>
					<div class="hl-detection-status__info">
						<template v-if="job.createdTags > 0">
							<div class="hl-detection-status__info-line">
								{{ $I18n.trans('highlights.audio_detection.tagging.summary_created', {
									created: job.createdTags,
									duration: getDuration(job.totalDuration, { appendUnit: true })
								}) }}
							</div>
						</template>
						<template v-if="job.ratedTags > 0">
							<div class="hl-detection-status__info-line">
								{{ $I18n.trans('highlights.audio_detection.tagging.summary_rated', {
									rated: job.ratedTags,
								}) }}
							</div>
						</template>
					</div>
				</template>
				<div
					v-else
					class="hl-detection-status__title"
				>
					{{ $I18n.trans('highlights.audio_detection.tagging.no_scenes') }}
				</div>
			</div>
		</div>
		<div
			v-else
			class="hl-detection-settings"
		>
			<Select
				v-model="settings.taggingMode"
				:clearable="false"
				:searchable="false"
				:dark-theme="true"
				name="taggingMode"
				:label="$I18n.trans('highlights.audio_detection.tagging.tagging_mode')"
				:info="taggingModeInfo"
				:options="taggingModeOptions"
			/>
			<InputToggle
				v-if="showTagOptimizationSettings"
				v-model="settings.optimizeClipDuration"
				:label="$I18n.trans('highlights.rules.iq.optimize_clip_duration')"
				:info="$I18n.trans('highlights.rules.iq.optimize_clip_duration_info')"
			/>
			<InputToggle
				v-if="showTagOptimizationSettings"
				v-model="settings.optimizeTagOffsets"
				:label="$I18n.trans('highlights.rules.iq.optimize_tag_offsets')"
				:info="$I18n.trans('highlights.rules.iq.optimize_tag_offsets_info')"
			/>
		</div>
		<div
			v-if="isFailed"
			class="mb-3"
		>
			{{ $I18n.trans('highlights.audio_detection.tagging.failed') }}
		</div>
		<button
			v-if="isIdle"
			class="button button--responsive button--primary"
			@click="initDetection"
		>
			{{ $I18n.trans('highlights.audio_detection.tagging.start') }}
		</button>
		<button
			v-if="isCreated || isProcessing"
			class="button button--responsive button--primary"
			@click="cancelJob"
		>
			{{ $I18n.trans('highlights.audio_detection.tagging.cancel') }}
		</button>
		<button
			v-else-if="isCompleted"
			class="button button--responsive button--primary"
			@click="close"
		>
			{{ transUcfirst('commons.close') }}
		</button>
	</div>
</template>
<script>
import Select from '@shared/components/Select';
import LoadingSpinner from '@shared/components/LoadingSpinner';
import InputToggle from '@shared/components/Form/InputToggle';
import Icon from '@shared/components/Icon';
import { mapActions, mapGetters } from 'vuex';
import { getDuration } from '@shared/utils/timeUtils';
import { TagName } from '@shared/enums/TagName';
import { HighlightDetectionTaggingMode } from '@shared/enums/HighlightDetectionTaggingMode';

const RATE_EXCLUDED_TAG_NAMES = [
	TagName.TIMING_CLOCK_START,
	TagName.TIMING_CLOCK_END,
	TagName.TIMING_CLOCK_SET,
];

export default {
	name:       'HighlightDetectionWidget',
	components: { Icon, LoadingSpinner, InputToggle, Select },
	data() {
		return {
			settings: {
				taggingMode:          HighlightDetectionTaggingMode.CREATE_AND_RATE,
				optimizeClipDuration: true,
				optimizeTagOffsets:   true,
				enhancedAi:           false,
			},
		};
	},
	computed: {
		...mapGetters({
			state:        'highlights/state',
			job:          'highlights/job',
			isProcessing: 'highlights/isProcessing',
			isCreated:    'highlights/isCreated',
			isCompleted:  'highlights/isCompleted',
			isIdle:       'highlights/isIdle',
			isFailed:     'highlights/isFailed',
			progressPct:  'highlights/progressPct',
			eventTags:    'event/tags',
		}),
		hasRateableTags() {
			return (this.eventTags ?? []).some(tag => !RATE_EXCLUDED_TAG_NAMES.includes(tag.tag?.name));
		},
		widgetActive() {
			return this.isCreated || this.isProcessing || this.isCompleted;
		},
		showTagOptimizationSettings() {
			return this.hasRateableTags;
		},
		taggingModeInfo() {
			if (this.hasRateableTags) {
				return 'When using detect & rate, new tags will be created and existing tags will be rated. When using "rate existing tags", no new tags will be added.';
			}

			return 'Video will be analyzed and tags will be created for detected highlight scenes';
		},
		taggingModeOptions() {
			const options = [{
				label: this.$I18n.trans('highlights.audio_detection.tagging.tagging_mode_create_and_rate'),
				value: HighlightDetectionTaggingMode.CREATE_AND_RATE,
			}];

			if (this.hasRateableTags) {
				options.push({
					label: this.$I18n.trans('highlights.audio_detection.tagging.tagging_mode_rate_existing'),
					value: HighlightDetectionTaggingMode.RATE_EXISTING,
				});
			}

			return options;
		},
	},
	watch: {
		taggingModeOptions: {
			handler(options) {
				if (!options.some(option => option.value === this.settings.taggingMode)) {
					this.settings.taggingMode = options[0]?.value ?? null;
				}
			},
			immediate: true,
		},
		isCompleted: {
			handler() {
				this.$emit('completed');
			},
			immediate: false,
		},
	},
	mounted() {
		this.loadJobByEvent();
	},
	methods: {
		...mapActions({
			startDetection: 'highlights/startDetection',
			cancelJob:      'highlights/cancelJob',
			loadJobByEvent: 'highlights/loadJobByEvent',
			close:          'highlights/close',
		}),
		getDuration,
		initDetection: function() {
			this.startDetection(this.settings);
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';

.hl-detection-status {
	display: flex;
	align-items: flex-start;
	gap: $sp4;
	margin-bottom: $sp5;
}

.hl-detection-status__leading {
	flex-shrink: 0;
	margin-top: 2px;
}

.hl-detection-status__text {
	flex: 1;
	min-width: 0;
}

.hl-detection-status__completed {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	gap: $sp4;
	flex: 1;
	min-width: 0;
	width: 100%;
}

.hl-detection-status__title {
	flex-shrink: 0;
}

.hl-detection-status__icon {
	flex-shrink: 0;
}

.hl-detection-status__info {
	display: flex;
	flex-direction: column;
	gap: $sp2;
	width: 100%;
}

.hl-detection-status__info-line {
	min-width: 0;
}
</style>
